import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { ChunkLoadErrorHandler } from "./core/chunk-load-error-handler";
import { AuthenticationGuard } from "./authentication/authentication-guard";
import { SubscriptionGuard } from "./authentication/subscription-guard";
import { OnboardingGuard } from "./authentication/onboarding-guard";
import { FeatureFlagGuard } from "./authentication/feature-flag.guard";
import { MobileOnlyGuard } from "./authentication/mobile-only.guard";
import { DialogAddScoreModule } from "./common/dialog-add-score/dialog-add-score.module";
import { DialogCodeinfoModule } from "./common/dialog-codeinfo/dialog-codeinfo.module";
import { DialogCommonModule } from "./common/dialog-common/dialog-common.module";
import { CardExpandedModule } from "./common/card-expanded/card-expanded.module";
import { ToolbarModule } from "./common/toolbar/toolbar.module";
import { LogoutModule } from "./pages/account/logout/logout.module";
import { ReferralModule } from "./pages/referral/referral.module";
import { SharedModules } from "./shared.modules";
// import { CardPickerModule } from './pages/card-picker/card-picker.module';
import {
  ErrorStateMatcher,
  MAT_DATE_LOCALE,
  ShowOnDirtyErrorStateMatcher,
} from "@angular/material/core";

import { ServiceWorkerModule } from "@angular/service-worker";
import { environment } from "../environments/environment";
import {
  AccountService,
  API_BASE_URL,
  AttributeService,
  CardsService,
  FlareUpPlanService,
  FormService,
  HistoryService,
  LearnModuleService,
  LicenceService,
  MedicationService,
  MessageService,
  OnboardingService,
  PatientPlanService,
  PerspectiveService,
  PhodaFormsService,
  SafetyPlanService,
  ScienceBitesService,
  ScoreService,
  SettingsService,
  ThoughtForTodayService,
  UserPromoFlagsService,
} from "../swagger/api.client.generated";
import { JwtInterceptor } from "./authentication/jwt.interceptor";
import { CanDeactivateGuard } from "./common/can-deactivate/can-deactivate.guard";
import { CardLocalService } from "./common/card/card.service";
import { DialogCardPickerModule } from "./common/dialog-card-picker/dialog-card-picker.module";
import { DialogConfirmDeleteModule } from "./common/dialog-confirm-delete/dialog-confirm-delete.module";
import { DialogFilterModule } from "./common/dialog-filter/dialog-filter.module";
import { DialogFinishPlanModule } from "./common/dialog-finish-plan/dialog-finish-plan.module";
import { DialogHaloCardsModule } from "./common/dialog-halo-cards/dialog-halo-cards.module";
import { DialogHistoryinfoModule } from "./common/dialog-historyinfo/dialog-historyinfo.module";
import { DialogNotificationsModule } from "./common/dialog-notifications/dialog-notifications.module";
import { DialogRegistrationCompleteModule } from "./common/dialog-registration-complete/dialog-registration-complete.module";
import { HelperFunctions } from "./common/functions/helper.functions";
import { LoadingButtonModule } from "./common/loading-button/loading-button.module";
import { PipesModule } from "./common/pipes/pipes.module";
import { VersionCheckService } from "./services/version-check.service";
// import { AuthInterceptor } from './authentication/authcheck.service';
import { DialogMediaPlayerModule } from "./common/dialog-media-player/dialog-media-player.module";
import { PromptModule } from "./common/prompt/prompt.module";
import { QuestionModule } from "./common/question/question.module";
import { SpinnerModule } from "./common/spinner/spinner.module";
import { WavySectionModule } from "./common/wavy-section/wavy-section.module";
import { SurveyModule } from "./common/survey/survey.module";
import { LoadingInterceptor } from "./services/loading.interceptor";
import { LoadingService } from "./services/loading.service";
import { PwaService } from "./services/pwa.service";
// tslint:disable-next-line:max-line-length
import { OverlayModule } from "@angular/cdk/overlay";
import { RouteReuseStrategy, Router } from "@angular/router";
import { CommunityRouteReuseStrategy } from "./community-route-reuse.strategy";
import * as Sentry from "@sentry/angular";
import { AngularSvgIconModule } from "angular-svg-icon";
import { DialogInfoModule } from "./common/dialog-info/dialog-info.module";
import { DialogNotificationService } from "./common/dialog-notifications/dialog-notifications.service";
import { DialogSimpleModule } from "./common/dialog-simple/dialog-simple.module";
import { DialogTopHalosModule } from "./common/dialog-top-halos/dialog-top-halos.module";
import { DialogSuperStarsModule } from "./common/dialog-super-stars/dialog-super-stars.module";
import { BottomNavComponent } from "./common/bottom-nav/bottom-nav.component";
import { SideNavComponent } from "./common/side-nav/side-nav.component";
// Temporarily disabled - see changelog/2026-07-20-disable-email-confirmation-feature.md
// import { EmailConfirmationBannerComponent } from "./common/email-confirmation-banner/email-confirmation-banner.component";
import { NovaInsightWidgetComponent } from "./common/nova-insight-widget/nova-insight-widget.component";
import { NovaCompanionFabComponent } from "./common/nova-companion-fab/nova-companion-fab.component";
import { LoginModule } from "./pages/account/login/login.module";
import { HomeService } from "./pages/home/home.service";
import { LandingNavigationComponent } from "./pages/landing-page/landing-navigation/landing-navigation.component";
import { SettingsLocalService } from "./pages/settings/settings.service";
import { ApplicationInsightsService } from "./services/application-insights.service";
import { MobileService } from "./services/mobile.service";

// const initializer = (pwaService: PwaService) => () => pwaService.initPwaPrompt();
const initializerMobileApp = (mobileService: MobileService) => () =>
  mobileService.init();

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,
    AngularSvgIconModule.forRoot(),
    PipesModule,
    SharedModules,
    DialogFilterModule,
    DialogNotificationsModule,
    LogoutModule,
    DialogCommonModule,
    DialogAddScoreModule,
    DialogTopHalosModule,
    DialogSuperStarsModule,
    DialogSimpleModule,
    DialogCodeinfoModule,
    ToolbarModule,
    ReferralModule,
    AppRoutingModule,
    CardExpandedModule,
    DialogHistoryinfoModule,
    DialogRegistrationCompleteModule,
    DialogConfirmDeleteModule,
    DialogCardPickerModule,
    LoadingButtonModule,
    DialogFinishPlanModule,
    DialogHaloCardsModule,
    DialogMediaPlayerModule,
    SpinnerModule,
    PromptModule,
    WavySectionModule,
    QuestionModule,
    SurveyModule,
    DialogInfoModule,
    LoginModule,
    // Custom service worker handles push notifications
    // Angular Service Worker (ngsw-worker.js) is disabled in favor of custom-sw.js
    // which provides push notification support with proper routing
    ServiceWorkerModule.register("custom-sw.js", {
      enabled: environment.production,
    }),
    // Import standalone components
    BottomNavComponent,
    SideNavComponent,
    LandingNavigationComponent,
    // Temporarily disabled - see changelog/2026-07-20-disable-email-confirmation-feature.md
    // EmailConfirmationBannerComponent,
    NovaInsightWidgetComponent,
    NovaCompanionFabComponent,
    AngularSvgIconModule,
    OverlayModule,
  ],
  providers: [
    AuthenticationGuard,
    SubscriptionGuard,
    OnboardingGuard,
    FeatureFlagGuard,
    MobileOnlyGuard,
    CardLocalService,
    CardsService,
    HelperFunctions,
    CanDeactivateGuard,
    VersionCheckService,
    LoadingService,
    FormService,
    MobileService,
    MedicationService,
    PwaService,
    ScoreService,
    AccountService,
    PhodaFormsService,
    PerspectiveService,
    ScienceBitesService,
    SettingsService,
    MessageService,
    ThoughtForTodayService,
    AttributeService,
    DialogNotificationService,
    HistoryService,
    SettingsLocalService,
    PatientPlanService,
    HomeService,
    ApplicationInsightsService,
    LearnModuleService,
    FlareUpPlanService,
    SafetyPlanService,
    LicenceService,
    OnboardingService,
    UserPromoFlagsService,
    { provide: RouteReuseStrategy, useClass: CommunityRouteReuseStrategy },
    { provide: API_BASE_URL, useFactory: () => (window as any).__mobileApiBaseUrl || environment.apiBaseUrl },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: ApplicationInsightsInterceptor,
    //   multi: true,
    // },
    // { provide: ErrorHandler, useClass: ApplicationInsightsErrorHandler },
    {
      provide: ErrorHandler,
      useClass: ChunkLoadErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    // { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    // { provide: APP_INITIALIZER, useFactory: initializer, deps: [PwaService], multi: true},
    {
      provide: APP_INITIALIZER,
      useFactory: initializerMobileApp,
      deps: [MobileService],
      multi: true,
    },
    { provide: MAT_DATE_LOCALE, useValue: "en-GB" },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
